import { useEffect, useState } from 'react'
import { api } from '../lib/api'

/**
 * Banner sticky no topo que mostra announcements ativos pro tenant
 * logado. Múltiplos banners empilham. Dismissão é persistente — chama
 * POST /api/announcements/:id/dismiss.
 *
 * Renderiza nada se não há token (não logado) ou se a chamada falhar.
 */

interface Announcement {
  id: string
  title: string
  body: string
  severity: 'INFO' | 'WARNING' | 'MAINTENANCE' | 'SUCCESS'
  dismissible: boolean
}

const STYLES: Record<Announcement['severity'], { bg: string; border: string; text: string; icon: string }> = {
  INFO:        { bg: 'bg-blue-50',  border: 'border-blue-200',  text: 'text-blue-900',  icon: 'ℹ' },
  WARNING:     { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', icon: '⚠' },
  MAINTENANCE: { bg: 'bg-red-50',   border: 'border-red-200',   text: 'text-red-900',   icon: '🔧' },
  SUCCESS:     { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900', icon: '✓' },
}

export default function AnnouncementsBanner() {
  const [items, setItems] = useState<Announcement[]>([])

  useEffect(() => {
    const token = localStorage.getItem('pakt_token')
    if (!token) return
    api.get<{ announcements: Announcement[] }>('/api/announcements/active')
      .then(r => setItems(r.announcements))
      .catch(() => null) // silencioso — banner não deve quebrar layout
  }, [])

  async function dismiss(id: string) {
    setItems(prev => prev.filter(a => a.id !== id)) // optimistic
    api.post(`/api/announcements/${id}/dismiss`, {}).catch(() => null)
  }

  if (items.length === 0) return null

  return (
    <div className="sticky top-0 z-40">
      {items.map(a => {
        const s = STYLES[a.severity]
        return (
          <div key={a.id} className={`${s.bg} ${s.border} border-b`}>
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-start gap-3">
              <span className={`${s.text} text-lg leading-none`}>{s.icon}</span>
              <div className={`flex-1 ${s.text} text-sm`}>
                <div className="font-semibold">{a.title}</div>
                <div className="mt-1 opacity-90" style={{ whiteSpace: 'pre-wrap' }}>{a.body}</div>
              </div>
              {a.dismissible && (
                <button
                  onClick={() => dismiss(a.id)}
                  className={`${s.text} hover:opacity-70 text-xl leading-none`}
                  aria-label="Fechar"
                  title="Fechar"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
